import { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useNavigate } from "react-router-dom"
import {
  Box, Button, Flex, HStack, Input, Select, Text, Badge, Spinner, Table, Tbody, Td, Th, Thead, Tr, useToast, Tabs, TabList, Tab, TabPanels, TabPanel,
  CircularProgress, CircularProgressLabel, Menu, MenuButton, MenuList, MenuItemOption, MenuOptionGroup, MenuDivider, MenuItem, Portal,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, NumberInput, NumberInputField, SimpleGrid,
} from "@chakra-ui/react"
import { FaFileExcel, FaChevronDown, FaRoute, FaSyncAlt, FaHome, FaMapMarkerAlt } from "react-icons/fa"

import PageToolbar from "../../components/shared/PageToolbar"
import { Panel } from "../../components/shared/Section"
import ExportMenu from "../../components/shared/ExportMenu"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { neutral, signal, type } from "../../theme/tokens"

/**
 * Rep tracker (Field section, 2026-10-06 — backend docs/michael/rep-field/TASK-rep-tracker.md).
 * Blue Mango's Georep replacement, admin side (Linda / masters): the Georep strike-rate card for one, several or
 * all reps over a date range (optionally one store), per-rep table with ROAD km, every visit, km per day per rep,
 * the reps' homes (an address replaces the stand-in) and a re-plan. Georep history (31 Aug – 18 Sep) is included.
 * 2026-10-07: renamed "Rep Routing"; a **Store addresses** tab (a store's map point from an address); org admins
 * (Linda) VIEW everything — the edit buttons show only when the server says `canEdit` (masters for now).
 * 2026-10-12 (Helena): also rendered `embedded` under Rep Sales on the combined /rep page — there a login the
 * server does not treat as a routing admin gets nothing (no redirect, no error panel) instead of this page. Embedded,
 * the Per rep tab is dropped: its columns join the Rep Sales table per rep (onSummary hands the summary up).
 */
const API = process.env.REACT_APP_API_URL
const uid = () => localStorage.getItem("userid")
const pct = (a, b) => (b ? Math.round((100 * a) / b) : 0)
const kmFmt = (v) => (v == null ? "–" : Math.round(Number(v)).toLocaleString("en-ZA"))
const STATUS_LABEL = { planned: "To visit", visited: "Visited", late: "Caught up / early", missed: "Missed", additional: "Additional" }
const monthStart = (d) => `${d.slice(0, 7)}-01`

const RepTracker = ({ embedded = false, header = null, onSummary }) => {
  const toast = useToast()
  const navigate = useNavigate()
  const { selectedOrg, isMaster } = useSupplierRetailer()
  const orgArg = useMemo(() => (isMaster ? { org_code: selectedOrg } : {}), [isMaster, selectedOrg])
  const post = useCallback((path, body = {}) => axios.post(`${API}/field/${path}`, { userid: uid(), ...orgArg, ...body }), [orgArg])

  const [me, setMe] = useState(null)
  const [error, setError] = useState(null)
  const [reps, setReps] = useState([]) // [] = all
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const [site, setSite] = useState("")
  const [sum, setSum] = useState(null)
  const [rows, setRows] = useState(null)
  const [busy, setBusy] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [edit, setEdit] = useState(null) // home being edited
  const [storeList, setStoreList] = useState(null) // Store addresses tab
  const [storeEdit, setStoreEdit] = useState(null)
  const [storeQ, setStoreQ] = useState("")
  const [missingOnly, setMissingOnly] = useState(true)

  useEffect(() => {
    post("me").then((r) => {
      const m = r.data.data
      if (m.role !== "admin") {
        if (embedded) return setError("hidden")
        return navigate("/field/route", { replace: true })
      }
      setMe(m)
      // Always the current month to today (Helena 2026-10-13: "make the default the current month (always)") — was from 31 Aug.
      setFrom(monthStart(m.today))
      setTo(m.today)
    }).catch((e) => setError(embedded ? "hidden" : e?.response?.data?.message || e.message))
  }, [post, navigate, embedded])

  const filters = useMemo(() => ({ reps, from, to, site_code: site || undefined }), [reps, from, to, site])
  const load = useCallback(() => {
    if (!me || !from || !to) return
    setSum(null); setRows(null)
    post("summary", filters).then((r) => setSum(r.data.data)).catch((e) => setError(e?.response?.data?.message || e.message))
    post("detail", filters).then((r) => setRows(r.data.data)).catch(() => setRows([]))
  }, [me, from, to, filters, post])
  useEffect(load, [load])
  // Embedded: the Rep Sales table's routing columns are this month (1st → month end, every rep; visits to date),
  // in step with its "MTD" sales column — independent of this section's own date filters (Helena, 2026-10-12).
  useEffect(() => {
    if (!me || !onSummary) return
    // Whole month: visits exist only up to today, and the month's planned km is the budget ("est km").
    const [y, m] = me.today.split("-").map(Number)
    const mtd = { reps: [], from: monthStart(me.today), to: `${me.today.slice(0, 7)}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}` }
    post("summary", mtd).then((r) => onSummary({ ...r.data.data, from: mtd.from, to: mtd.to })).catch(() => onSummary(null))
  }, [me, post, onSummary])

  const replan = async () => {
    setBusy(true)
    try {
      const r = await post("replan", { rep: "ALL" })
      toast({ title: "Routes re-planned", description: `${r.data.data.length} reps, from today to month end`, status: "success", isClosable: true })
      load()
    } catch (e) {
      toast({ title: "Re-plan failed", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setBusy(false) }
  }
  const saveHome = async () => {
    setBusy(true)
    try {
      const body = { rep: edit.rep_name, label: edit.label, stores_per_day: edit.stores_per_day }
      if (edit.lat !== "" && edit.lng !== "") { body.lat = Number(edit.lat); body.lng = Number(edit.lng) }
      const r = await post("home", body)
      toast({ title: "Home saved and route re-planned", description: `${r.data.data.plan.planned + r.data.data.plan.repeats} visits · ${r.data.data.plan.km} road km to month end`, status: "success", isClosable: true })
      setEdit(null); load()
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setBusy(false) }
  }
  const loadStores = useCallback(() => {
    if (!me) return
    setStoreList(null)
    post("stores").then((r) => setStoreList(r.data.data || [])).catch(() => setStoreList([]))
  }, [me, post])
  useEffect(loadStores, [loadStores])
  const saveStore = async () => {
    setBusy(true)
    try {
      const body = { site_code: storeEdit.site_code, retailer: storeEdit.retailer, address: storeEdit.address }
      if (storeEdit.lat !== "" && storeEdit.lng !== "") { body.lat = Number(storeEdit.lat); body.lng = Number(storeEdit.lng) }
      const r = await post("store-address", body)
      toast({ title: "Store location saved", description: `Re-planned from tomorrow: ${r.data.data.reps.join(", ")}`, status: "success", isClosable: true })
      setStoreEdit(null); loadStores(); load()
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setBusy(false) }
  }
  const exportXlsx = async () => {
    setExporting(true)
    try {
      const res = await fetch(`${API}/field/export`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userid: uid(), ...orgArg, ...filters }) })
      if (!res.ok || !(res.headers.get("content-type") || "").includes("spreadsheetml")) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `Rep Routing ${from} to ${to}.xlsx`
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href)
    } catch (e) {
      toast({ title: "Download failed", description: e.message, status: "error", isClosable: true })
    } finally { setExporting(false) }
  }

  if (error === "hidden") return null
  if (embedded && !me) return null
  if (error) return <Panel><Text fontSize={type.size.small} color={signal.down}>{error}</Text></Panel>
  if (!me) return <Panel><Spinner size="sm" /></Panel>
  const t = sum?.total
  const strike = t ? pct(t.visited + t.late, t.scheduled) : 0
  const kmByRep = new Map((sum?.perRep || []).map((r) => [r.rep_name, r]))
  const kmDays = sum?.kmDays || []

  return (
    <Box>
      {header}
      <PageToolbar
        description={embedded ? `Rep Routing${me.canEdit ? "" : " · view only"}` : `${me.orgName} · Rep Routing${me.canEdit ? "" : " · view only"}`}
        filters={
          <>
            <Menu closeOnSelect={false}>
              <MenuButton as={Button} size="sm" variant="outline" rightIcon={<FaChevronDown />} fontWeight={400}>
                {reps.length === 0 ? "All reps" : reps.length === 1 ? reps[0] : `${reps.length} reps`}
              </MenuButton>
              <Portal>
                <MenuList maxH="360px" overflowY="auto" zIndex={1500}>
                  <MenuItem onClick={() => setReps([])}>All reps</MenuItem>
                  <MenuDivider />
                  <MenuOptionGroup type="checkbox" value={reps} onChange={(v) => setReps(Array.isArray(v) ? v : [v])}>
                    {me.reps.map((r) => <MenuItemOption key={r} value={r}>{r}</MenuItemOption>)}
                  </MenuOptionGroup>
                </MenuList>
              </Portal>
            </Menu>
            <Input size="sm" type="date" w="auto" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Text fontSize={type.size.small} color={neutral[500]}>to</Text>
            <Input size="sm" type="date" w="auto" value={to} onChange={(e) => setTo(e.target.value)} />
            <Select size="sm" w="auto" maxW="300px" value={site} onChange={(e) => setSite(e.target.value)}>
              <option value="">All stores</option>
              {(sum?.sites || []).map((s) => <option key={s.site_code} value={s.site_code}>{s.site_description} ({s.site_code})</option>)}
            </Select>
          </>
        }
        actions={
          <HStack>
            {me.canEdit && <Button size="sm" variant="outline" leftIcon={<FaSyncAlt />} onClick={replan} isLoading={busy}>Re-plan routes</Button>}
            <ExportMenu isLoading={exporting} width="260px" items={[{ label: "Excel · strike rate, visits, km", onClick: exportXlsx, icon: FaFileExcel }]} />
          </HStack>
        }
      />

      <Panel mb={4}>
        <Text fontWeight={700} fontSize={type.size.small} letterSpacing="0.04em" color={neutral[600]} mb={3}>STRIKE RATE</Text>
        {!t ? <Spinner size="sm" /> : (
          <Flex gap={8} align="center" wrap="wrap">
            <CircularProgress value={strike} size="120px" thickness="10px" color="blue.700" trackColor={neutral[100]}>
              <CircularProgressLabel fontSize="1.6rem" fontWeight={600} color="blue.800">{strike}%</CircularProgressLabel>
            </CircularProgress>
            <SimpleGrid columns={[1, 2]} spacingX={10} spacingY={2} minW="300px">
              {[["Scheduled visits", t.scheduled], ["Visited on schedule", t.visited + t.late], ["of which caught up / early", t.late], ["Additional visits", t.additional], ["Missed visits", t.missed], ["Still to visit today", t.open_today]].map(([k, v]) => (
                <Flex key={k} justify="space-between" borderBottom="1px solid" borderColor={neutral[200]} py={1} gap={6}>
                  <Text fontSize={type.size.small} color={neutral[600]}>{k}</Text>
                  <Text fontSize={type.size.body} fontWeight={700} color="blue.800">{v.toLocaleString("en-ZA")}</Text>
                </Flex>
              ))}
            </SimpleGrid>
          </Flex>
        )}
      </Panel>

      <Tabs variant="enclosed" colorScheme="teal" isLazy>
        <TabList flexWrap="wrap">
          {!embedded && <Tab fontSize={type.size.small}>Per rep</Tab>}
          <Tab fontSize={type.size.small}>Visits</Tab>
          <Tab fontSize={type.size.small}>Km per day</Tab>
          <Tab fontSize={type.size.small}>Reps &amp; homes</Tab>
          <Tab fontSize={type.size.small}>Store addresses</Tab>
        </TabList>
        <TabPanels>
          {!embedded && <TabPanel px={0}>
            <Panel>
              {!sum ? <Spinner size="sm" /> : (
                <Box overflowX="auto">
                  <Table size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1.5 } }}>
                    <Thead bg={neutral[50]}><Tr><Th>Rep</Th><Th isNumeric>Scheduled</Th><Th isNumeric>Visited on schedule</Th><Th isNumeric>Caught up / early</Th><Th isNumeric>Additional</Th><Th isNumeric>Missed</Th><Th isNumeric>Strike rate</Th><Th isNumeric>Road km</Th><Th isNumeric>Days</Th><Th></Th></Tr></Thead>
                    <Tbody>
                      {sum.perRep.map((r) => (
                        <Tr key={r.rep_name}>
                          <Td fontWeight={600}>{r.rep_name}</Td>
                          <Td isNumeric>{r.scheduled}</Td><Td isNumeric>{r.visited + r.late}</Td><Td isNumeric>{r.late}</Td><Td isNumeric>{r.additional}</Td>
                          <Td isNumeric color={r.missed ? signal.down : undefined}>{r.missed}</Td>
                          <Td isNumeric fontWeight={700}>{pct(r.visited + r.late, r.scheduled)}%</Td>
                          <Td isNumeric>{kmFmt(r.km)}</Td><Td isNumeric>{r.days}</Td>
                          <Td><Button size="xs" variant="ghost" leftIcon={<FaRoute />} onClick={() => navigate(`/field/route?rep=${encodeURIComponent(r.rep_name)}`)}>Route</Button></Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Panel>
          </TabPanel>}

          <TabPanel px={0}>
            <Panel>
              {!rows ? <Spinner size="sm" /> : (
                <Box overflowX="auto" maxH="620px" overflowY="auto">
                  <Table size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1 } }}>
                    <Thead bg={neutral[50]} position="sticky" top={0} zIndex={1}><Tr><Th>Date</Th><Th>Rep</Th><Th>#</Th><Th>Store</Th><Th>Visit</Th><Th>Result</Th><Th isNumeric>Leg km</Th><Th>Comment</Th><Th>Source</Th></Tr></Thead>
                    <Tbody>
                      {rows.slice(0, 1500).map((r) => (
                        <Tr key={r.id}>
                          <Td whiteSpace="nowrap">{r.plan_date}</Td><Td whiteSpace="nowrap">{r.rep_name}</Td><Td>{r.status === "additional" ? "+" : r.seq}</Td>
                          <Td>{r.site_description}{r.site_code ? ` (${r.site_code})` : ""}</Td>
                          <Td>{r.visit_kind === "repeat" ? "2nd" : "1st"}</Td>
                          <Td><Badge colorScheme={{ visited: "green", late: "teal", missed: "red", additional: "purple" }[r.status] || "gray"}>{STATUS_LABEL[r.status] || r.status}</Badge></Td>
                          <Td isNumeric>{r.leg_km == null ? "" : Number(r.leg_km).toFixed(1)}</Td>
                          <Td maxW="260px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={r.comment || ""}>{r.comment}</Td>
                          <Td>{r.source === "georep" ? "Georep" : "Plan"}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {rows.length > 1500 && <Text fontSize={type.size.caption} color={neutral[500]} mt={2}>Showing 1,500 of {rows.length} — narrow the filters or download Excel.</Text>}
                </Box>
              )}
            </Panel>
          </TabPanel>

          <TabPanel px={0}>
            <Panel>
              {!sum ? <Spinner size="sm" /> : kmDays.length === 0 ? <Text fontSize={type.size.small} color={neutral[500]}>No planned days in this range (Georep history carries no road km).</Text> : (
                <Box overflowX="auto" maxH="620px" overflowY="auto">
                  <Table size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1 } }}>
                    <Thead bg={neutral[50]} position="sticky" top={0}><Tr><Th>Rep</Th><Th>Date</Th><Th isNumeric>Stores</Th><Th isNumeric>Road km</Th></Tr></Thead>
                    <Tbody>
                      {[...new Set(kmDays.map((d) => d.rep_name))].sort().map((rep) => {
                        const ds = kmDays.filter((d) => d.rep_name === rep)
                        return [
                          ...ds.map((d) => (
                            <Tr key={`${rep}${d.plan_date}`}><Td>{rep}</Td><Td>{d.plan_date}</Td><Td isNumeric>{d.stores}</Td><Td isNumeric>{Number(d.km).toFixed(0)}{d.km_source === "estimate" ? "*" : ""}</Td></Tr>
                          )),
                          <Tr key={`${rep}-total`} bg={neutral[50]}><Td fontWeight={700}>{rep} — total</Td><Td></Td><Td isNumeric fontWeight={700}>{ds.reduce((a, d) => a + d.stores, 0)}</Td><Td isNumeric fontWeight={700}>{kmFmt(ds.reduce((a, d) => a + Number(d.km || 0), 0))}</Td></Tr>,
                        ]
                      })}
                    </Tbody>
                  </Table>
                  <Text fontSize={type.size.caption} color={neutral[500]} mt={2}>Road km from home through the day's stores and back home (OpenStreetMap roads). * = estimated (the road service was unavailable).</Text>
                </Box>
              )}
            </Panel>
          </TabPanel>

          <TabPanel px={0}>
            <Panel>
              {!sum ? <Spinner size="sm" /> : (
                <Box overflowX="auto">
                  <Table size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1.5 } }}>
                    <Thead bg={neutral[50]}><Tr><Th>Rep</Th><Th>Home</Th><Th isNumeric>Stores / day</Th><Th>Login</Th><Th>Planned on</Th><Th isNumeric>Road km (range)</Th><Th></Th></Tr></Thead>
                    <Tbody>
                      {sum.homes.map((h) => (
                        <Tr key={h.rep_name}>
                          <Td fontWeight={600}>{h.rep_name}</Td>
                          <Td>{h.home_label || "–"} {h.home_dummy && h.home_lat != null && <Badge colorScheme="orange" ml={1}>stand-in</Badge>}</Td>
                          <Td isNumeric>{h.stores_per_day}</Td>
                          <Td>{h.userid ? <Badge colorScheme="green">yes</Badge> : <Badge>no</Badge>}</Td>
                          <Td>{h.replanned_on || "–"}</Td>
                          <Td isNumeric>{kmFmt(kmByRep.get(h.rep_name)?.km)}</Td>
                          <Td>{me.canEdit && <Button size="xs" variant="outline" leftIcon={<FaHome />} onClick={() => setEdit({ rep_name: h.rep_name, label: h.home_dummy ? "" : h.home_label || "", lat: "", lng: "", stores_per_day: h.stores_per_day })}>Set home</Button>}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Panel>
          </TabPanel>

          <TabPanel px={0}>
            <Panel>
              {!storeList ? <Spinner size="sm" /> : (() => {
                const q = storeQ.trim().toLowerCase()
                const missing = storeList.filter((s) => s.lat == null).length
                const list = storeList.filter((s) => (!missingOnly || s.lat == null) &&
                  (!q || `${s.site_code} ${s.site_description} ${s.town || ""} ${s.reps || ""}`.toLowerCase().includes(q)))
                return (
                  <>
                    <Flex gap={2} mb={3} wrap="wrap" align="center">
                      <Input size="sm" maxW="320px" placeholder="Search store, town or rep" value={storeQ} onChange={(e) => setStoreQ(e.target.value)} />
                      <Button size="sm" variant={missingOnly ? "solid" : "outline"} colorScheme={missingOnly ? "red" : "gray"} onClick={() => setMissingOnly(true)}>No map location ({missing})</Button>
                      <Button size="sm" variant={!missingOnly ? "solid" : "outline"} colorScheme={!missingOnly ? "teal" : "gray"} onClick={() => setMissingOnly(false)}>All stores ({storeList.length})</Button>
                    </Flex>
                    <Text fontSize={type.size.caption} color={neutral[500]} mb={2}>
                      A store without a map location is left out of the routes. {me.canEdit ? "Set an address (or the coordinates) — the reps with that store are re-planned from tomorrow." : "Spear sets the addresses for now."}
                    </Text>
                    <Box overflowX="auto" maxH="620px" overflowY="auto">
                      <Table size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1.5 } }}>
                        <Thead bg={neutral[50]} position="sticky" top={0} zIndex={1}><Tr><Th>Store</Th><Th>Town</Th><Th>Rep(s)</Th><Th>Location</Th><Th></Th></Tr></Thead>
                        <Tbody>
                          {list.map((s) => (
                            <Tr key={`${s.retailer}|${s.site_code}`}>
                              <Td><Text fontWeight={600}>{s.site_description}</Text><Text color={neutral[500]}>{s.retailer} · {s.site_code}</Text></Td>
                              <Td>{[s.town, s.province].filter(Boolean).join(", ") || "–"}</Td>
                              <Td maxW="220px" whiteSpace="normal">{s.reps}</Td>
                              <Td>
                                {s.lat == null ? <Badge colorScheme="red">No map location</Badge> : (
                                  <>
                                    <Badge colorScheme={s.source === "manual" ? "green" : "gray"}>{s.source === "manual" ? "Set by Spear" : "Found automatically"}</Badge>
                                    {s.source === "manual" && s.address && <Text color={neutral[500]} mt={1}>{s.address}</Text>}
                                  </>
                                )}
                              </Td>
                              <Td>{me.canEdit && <Button size="xs" variant="outline" leftIcon={<FaMapMarkerAlt />} onClick={() => setStoreEdit({ ...s, address: s.source === "manual" ? s.address || "" : "", lat: "", lng: "" })}>Set address</Button>}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                      {list.length === 0 && <Text fontSize={type.size.small} color={neutral[500]} mt={2}>Nothing to show.</Text>}
                    </Box>
                  </>
                )
              })()}
            </Panel>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Modal isOpen={!!storeEdit} onClose={() => setStoreEdit(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Store address — {storeEdit?.site_description}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize={type.size.small} color={neutral[600]} mb={2}>Street address (found on the map), or the coordinates. Saving re-plans the reps with this store from tomorrow.</Text>
            <Input mb={3} placeholder="e.g. Shop 12, Canal Walk, Century Boulevard, Cape Town" value={storeEdit?.address || ""} onChange={(e) => setStoreEdit({ ...storeEdit, address: e.target.value })} />
            <HStack>
              <Input placeholder="Latitude (optional)" value={storeEdit?.lat || ""} onChange={(e) => setStoreEdit({ ...storeEdit, lat: e.target.value })} />
              <Input placeholder="Longitude (optional)" value={storeEdit?.lng || ""} onChange={(e) => setStoreEdit({ ...storeEdit, lng: e.target.value })} />
            </HStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setStoreEdit(null)}>Cancel</Button>
            <Button colorScheme="teal" onClick={saveStore} isLoading={busy} isDisabled={!storeEdit?.address && (storeEdit?.lat === "" || storeEdit?.lng === "")}>Save &amp; re-plan</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!edit} onClose={() => setEdit(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Home — {edit?.rep_name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize={type.size.small} color={neutral[600]} mb={2}>Street address (found on the map), or the coordinates. Saving re-plans this rep from today.</Text>
            <Input mb={3} placeholder="e.g. 12 Main Road, Constantia, Cape Town" value={edit?.label || ""} onChange={(e) => setEdit({ ...edit, label: e.target.value })} />
            <HStack mb={3}>
              <Input placeholder="Latitude (optional)" value={edit?.lat || ""} onChange={(e) => setEdit({ ...edit, lat: e.target.value })} />
              <Input placeholder="Longitude (optional)" value={edit?.lng || ""} onChange={(e) => setEdit({ ...edit, lng: e.target.value })} />
            </HStack>
            <Text fontSize={type.size.small} color={neutral[600]} mb={1}>Stores per day</Text>
            <NumberInput min={1} max={15} value={edit?.stores_per_day || 6} onChange={(v) => setEdit({ ...edit, stores_per_day: Number(v) })} maxW="120px"><NumberInputField /></NumberInput>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setEdit(null)}>Cancel</Button>
            <Button colorScheme="teal" onClick={saveHome} isLoading={busy} isDisabled={!edit?.label && (edit?.lat === "" || edit?.lng === "")}>Save &amp; re-plan</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default RepTracker
